import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { INSIGHTS_STATUS, LocationIdSchema } from '@brightlocal/shared';

export const InsightsStatusSchema = z.nativeEnum(INSIGHTS_STATUS);

export type InsightsStatus = z.infer<typeof InsightsStatusSchema>;

export const InsightsStatusResponseSchema = z.object({
  status: InsightsStatusSchema
});

export type InsightsStatusResponse = z.infer<typeof InsightsStatusResponseSchema>;

export const BrainRecommendationActionSchema = z.object({
  step: z.string(),
  tags: z.array(z.string()).optional()
});

export type BrainRecommendationAction = z.infer<typeof BrainRecommendationActionSchema>;

const BrainRecommendationSchema = z.object({
  insight_title: z.string(),
  themes: z.array(z.string()),
  action: z.array(BrainRecommendationActionSchema)
});

export const BrainRecommendationsResponseSchema = z
  .object({
    last_update: z.string().nullable(),
    business_analysis: z
      .object({
        executive_summary: z.string().optional(),
        recommendations: z.array(BrainRecommendationSchema)
      })
      .nullable()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type BrainRecommendationsResponse = z.infer<typeof BrainRecommendationsResponseSchema>;

/** A node in the location score breakdown tree; `breakdown` is absent for leaf-level metrics. */
export interface LocationScoreBreakdownItem {
  /** Identifier of this breakdown component, e.g. "gbp" or "keyword_coverage". Not a closed set — BE can add new ones. */
  id: string;
  /** Score for this component, 0–100. */
  score: number;
  /** Nested breakdown items. Absent for leaf-level metrics. */
  breakdown?: LocationScoreBreakdownItem[];
}

// Recursive shape needs an explicit z.ZodType annotation — z.infer can't resolve a self-referencing schema.
export const LocationScoreBreakdownItemSchema: z.ZodType<LocationScoreBreakdownItem> = z.lazy(() =>
  z.object({
    id: z.string(),
    score: z.number().min(0).max(100),
    breakdown: z.array(LocationScoreBreakdownItemSchema).optional()
  })
);

/** Validates and transforms the API response for a location's overall score and breakdown. */
export const LocationScoreResponseSchema = z
  .object({
    location_id: LocationIdSchema,
    score: z.number().min(0).max(100).nullable(),
    breakdown: z.array(LocationScoreBreakdownItemSchema).nullable()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type LocationScoreResponse = z.infer<typeof LocationScoreResponseSchema>;
